import { REPLACE_REGEX, transformInsertDataReplace } from '@ply-io/node-input-parser';

import { isNullOrUndefinedOrEmpty, isUUID } from './helpers';
import {
	GLOBAL_RUN_DATA,
	CONFIGURATIONS_FIELDS_KEY,
	CONFIGURATIONS_FIELDS,
	VIRTUAL_FIELDS,
	GLOBAL_DATA_PREFIX,
	CONTEXT_NODE_FIELD_KEY,
} from './workflow-node.const';
import FormulasService from '../components/common/RichTextBox/FormulaChip/formulas-service';
import { capitalizeWordsInLine } from '@/utils/language-service';
const ONE_TIME_REPLACE_REGEX = new RegExp(REPLACE_REGEX, ''); // init the flags of REPLACE_REGEX
const SEPARATOR = '.';
const MAPPER_REQUIRED_PARAMS = 'params_map'; //use only with user schema mapping response

const buildFieldKey = (param, paramsMap) =>
	`${param.key}${JSON.stringify(
		(param.depends_on || []).reduce((acc, dependsOnParamKey) => ({ ...acc, [dependsOnParamKey]: paramsMap[dependsOnParamKey] }), {})
	)}`;

// returns true if at least one of the depends_on params is not set
const getIsDisabled = (param, paramsMap) => (param.depends_on || []).some((paramKey) => isNullOrUndefinedOrEmpty(paramsMap[paramKey]));

export const PLY_PARAMS = {
	listSourceParam: {
		key: 'ply_list_source',
		type: 'string',
		description: 'Choose table',
	},
	textParserTemplateSourceParam: {
		key: 'ply_template_source',
		type: 'textparsersource',
		description: 'Choose template',
	},
	textParserParam: {
		key: 'ply_text_parser',
		type: 'textparser',
		description: 'Choose template',
	},
	emailTemplatesParam: {
		key: 'template',
		type: 'emailtemplate',
		description: 'Enter value',
	},
};

const formatPropertyKeyToReadable = (key = '', shouldFormat = false) => {
	key = key || ''; // in case key === null
	if (!shouldFormat) {
		return key;
	}
	key = key.replaceAll('_', ' ');
	key = key.replaceAll('.', ' » ');
	return capitalizeWordsInLine(key);
};

const buildFieldPath = (key, path) => `${key}.${path}`;

const buildKeyForPath = (rootNode, key, path, formulaName = null, formulaParams = []) => {
	const localKey = CONFIGURATIONS_FIELDS.includes(key) ? GLOBAL_RUN_DATA : CONTEXT_NODE_FIELD_KEY == key ? rootNode.uuid : key;
	const localPath = VIRTUAL_FIELDS.includes(key)
		? buildFieldPath(CONFIGURATIONS_FIELDS.includes(key) ? CONFIGURATIONS_FIELDS_KEY : key, path)
		: path;
	const keyForPath = `{{${localKey}__${localPath}}}`;
	return formulaName ? FormulasService.wrapWithFormula(formulaName, keyForPath, ...formulaParams) : keyForPath;
};

const buildKeyForTableColumn = (columnKey) => `{{${columnKey}}}`;
const getValueFromTableColumnKey = (columnKey) => removeBracketsFromValue(columnKey);

const isPathNeedTransform = (path, mappingBlocksUUIDs) => isPathOfMapping(path, mappingBlocksUUIDs);

const isPathOfMapping = (path = '', mappingBlocksUUIDs) => {
	const splittedPath = path.split('.');
	const mappingBlockUUID = splittedPath[0];
	return splittedPath.length > 1 && isUUID(mappingBlockUUID) && mappingBlocksUUIDs?.includes(mappingBlockUUID);
};

const extractPathFromValue = (value, insertDataReplaceDict, mappingBlocksUUIDs) => {
	if (!value) {
		return null;
	}
	if (value.includes(GLOBAL_DATA_PREFIX)) {
		return removeBracketsFromValue(value.replace(GLOBAL_DATA_PREFIX, ''));
	}
	const regResult = ONE_TIME_REPLACE_REGEX.exec(value);
	const path = regResult?.groups.path;
	if (isPathNeedTransform(path, mappingBlocksUUIDs)) {
		return transformInsertDataReplace(path, insertDataReplaceDict);
	}
	return path;
};

const extractNodeUUIDFromValue = (value) => {
	if (!value) {
		return null;
	}
	const regResult = ONE_TIME_REPLACE_REGEX.exec(value);
	return regResult?.groups.nodeUUID;
};

const extractNodeIdFromValue = (value, getNodeByUuidForSelectedWorkflowVersion) => {
	const nodeUUID = extractNodeUUIDFromValue(value);
	return nodeUUID ? getNodeByUuidForSelectedWorkflowVersion(nodeUUID)?.id : null;
};

const removeBracketsFromValue = (value) => {
	if (!value) {
		return null;
	}
	const openBrackets = '{{';
	const closeBrackets = '}}';
	const openBracketsIndex = value.indexOf(openBrackets);
	const closeBracketsIndex = value.lastIndexOf(closeBrackets);
	return value.substring(openBracketsIndex + openBrackets.length, closeBracketsIndex);
};

const extractSourceAction = (param) => param.source.split('.')[0];

const isPathOfConfiguration = (path) => path.startsWith(CONFIGURATIONS_FIELDS_KEY);

// ********** mapper **********
const getParamsForMapping = (service, action) => getParamsForMappingByItem(service, action.mapped_resource); // example getParamsForMappingByItem(smartsuiteServiceObject, getRecordActionObject)
const getParamsForMappingByItem = (service, item) => getMappingByItem(service, item)?.params || []; // example getParamsForMappingByItem(smartsuiteServiceObject, 'Application')
const getParamsSourceForMapping = (service, item) => getMappingByItem(service, item)?.map_source;
const getSkipKeysForMapping = (service, action) => getMappingByItem(service, action.mapped_resource)?.skip_mapping_keys || [];
//private
const getMappingByItem = (service, item) => service.param_mappings?.[item]; // internal func
// ********** mapper **********

const separateKey = (key) => key.split(SEPARATOR);
const joinKeys = (keys) => keys.join(SEPARATOR);

const ParamsService = {
	MAPPER_REQUIRED_PARAMS,

	buildFieldKey,
	getIsDisabled,
	buildKeyForPath,
	extractPathFromValue,
	extractNodeIdFromValue,
	extractNodeUUIDFromValue,
	PLY_PARAMS,
	removeBracketsFromValue,
	extractSourceAction,
	buildKeyForTableColumn,
	getValueFromTableColumnKey,
	formatPropertyKeyToReadable,
	isPathOfConfiguration,

	getParamsForMapping,
	getParamsForMappingByItem,
	getParamsSourceForMapping,
	getSkipKeysForMapping,

	separateKey,
	joinKeys,
};

export default ParamsService;
